import { useState, useCallback } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import axios from "axios";
import { toast } from "sonner";
import { CloudArrowUp, CheckCircle, XCircle, Warning, Image as ImageIcon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/apiError";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { PageHeader } from "@/components/PageHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BATCH_SIZE = 25; // Images are compressed client-side, so we can send more per batch
const MAX_IMAGE_WIDTH = 800; // Compress images to max 800px width
const IMAGE_QUALITY = 0.7; // 70% quality for JPEG compression

// Compress image in browser before upload
const compressImage = (file) => {
  return new Promise((resolve) => {
    // If file is already small (< 200KB), don't compress
    if (file.size < 200 * 1024) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // Scale down if larger than MAX_IMAGE_WIDTH
        if (width > MAX_IMAGE_WIDTH) {
          height = (height * MAX_IMAGE_WIDTH) / width;
          width = MAX_IMAGE_WIDTH;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            // Create new file with original name
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          'image/jpeg',
          IMAGE_QUALITY
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

export default function BulkUpload() {
  const [productFiles, setProductFiles] = useState([]);
  const [logoFiles, setLogoFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState(null);

  const handleProductDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || e.target.files || []);
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    setProductFiles(prev => [...prev, ...imageFiles]);
  }, []);

  const handleLogoDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || e.target.files || []);
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    setLogoFiles(prev => [...prev, ...imageFiles]);
  }, []);

  const removeProductFile = (index) => {
    setProductFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeLogoFile = (index) => {
    setLogoFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadProducts = async () => {
    if (productFiles.length === 0) {
      toast.error("Selecciona imágenes primero");
      return;
    }

    setUploading(true);
    setResults(null);
    
    // Aggregate results from all batches
    const aggregatedResults = {
      success: [],
      not_found: [],
      errors: []
    };

    const totalFiles = productFiles.length;
    
    // First, compress all images
    toast.info("Comprimiendo imágenes...");
    const compressedFiles = [];
    for (let i = 0; i < productFiles.length; i++) {
      try {
        const compressed = await compressImage(productFiles[i]);
        compressedFiles.push(compressed);
      } catch (err) {
        compressedFiles.push(productFiles[i]); // Use original if compression fails
      }
      // Update progress during compression
      setUploadProgress({ current: i + 1, total: totalFiles, phase: 'compressing' });
    }
    
    toast.info("Subiendo imágenes...");
    
    const batches = [];
    // Split compressed files into batches
    for (let i = 0; i < compressedFiles.length; i += BATCH_SIZE) {
      batches.push(compressedFiles.slice(i, i + BATCH_SIZE));
    }

    setUploadProgress({ current: 0, total: totalFiles, phase: 'uploading' });

    try {
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        const formData = new FormData();
        batch.forEach(file => {
          formData.append('files', file);
        });

        try {
          const response = await axios.post(`${API}/products/bulk-upload-images`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 180000 // 3 minutes timeout per batch
          });

          // Aggregate results
          if (response.data.details?.success) {
            aggregatedResults.success.push(...response.data.details.success);
          }
          if (response.data.details?.not_found) {
            aggregatedResults.not_found.push(...response.data.details.not_found);
          }
          if (response.data.details?.errors) {
            aggregatedResults.errors.push(...response.data.details.errors);
          }
          
        } catch (batchError) {
          // If a batch fails, mark all files in that batch as errors
          batch.forEach(file => {
            aggregatedResults.errors.push({
              filename: file.name,
              error: batchError.message || "Error de red"
            });
          });
        }

        // Update progress
        const processed = Math.min((batchIndex + 1) * BATCH_SIZE, totalFiles);
        setUploadProgress({ current: processed, total: totalFiles, phase: 'uploading' });
        
        // Small delay between batches to not overwhelm server
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 800));
        }
      }

      // Set final results
      const finalResults = {
        message: `Procesados ${totalFiles} archivos`,
        success_count: aggregatedResults.success.length,
        not_found_count: aggregatedResults.not_found.length,
        error_count: aggregatedResults.errors.length,
        details: aggregatedResults
      };
      
      setResults(finalResults);
      
      if (finalResults.success_count > 0) {
        toast.success(`${finalResults.success_count} imágenes actualizadas con éxito!`);
      }
      if (finalResults.not_found_count > 0) {
        toast.warning(`${finalResults.not_found_count} productos no encontrados`);
      }
      if (finalResults.error_count > 0) {
        toast.error(`${finalResults.error_count} errores durante la subida`);
      }

      // Clear files
      setProductFiles([]);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Error en la subida: " + getApiErrorMessage(error));
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const uploadLogos = async () => {
    if (logoFiles.length === 0) {
      toast.error("Selecciona imágenes primero");
      return;
    }

    setUploading(true);
    setResults(null);
    
    const aggregatedResults = {
      success: [],
      not_found: [],
      errors: []
    };

    const totalFiles = logoFiles.length;
    
    // Compress all images first
    toast.info("Comprimiendo logos...");
    const compressedFiles = [];
    for (let i = 0; i < logoFiles.length; i++) {
      try {
        const compressed = await compressImage(logoFiles[i]);
        compressedFiles.push(compressed);
      } catch (err) {
        compressedFiles.push(logoFiles[i]);
      }
      setUploadProgress({ current: i + 1, total: totalFiles, phase: 'compressing' });
    }
    
    toast.info("Subiendo logos...");
    
    const batches = [];
    for (let i = 0; i < compressedFiles.length; i += BATCH_SIZE) {
      batches.push(compressedFiles.slice(i, i + BATCH_SIZE));
    }

    setUploadProgress({ current: 0, total: totalFiles, phase: 'uploading' });

    try {
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        const formData = new FormData();
        batch.forEach(file => {
          formData.append('files', file);
        });

        try {
          const response = await axios.post(`${API}/clients/bulk-upload-logos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 180000
          });

          if (response.data.details?.success) {
            aggregatedResults.success.push(...response.data.details.success);
          }
          if (response.data.details?.not_found) {
            aggregatedResults.not_found.push(...response.data.details.not_found);
          }
          if (response.data.details?.errors) {
            aggregatedResults.errors.push(...response.data.details.errors);
          }
        } catch (batchError) {
          batch.forEach(file => {
            aggregatedResults.errors.push({
              filename: file.name,
              error: batchError.message || "Error de red"
            });
          });
        }

        const processed = Math.min((batchIndex + 1) * BATCH_SIZE, totalFiles);
        setUploadProgress({ current: processed, total: totalFiles, phase: 'uploading' });
        
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 800));
        }
      }

      const finalResults = {
        message: `Procesados ${totalFiles} archivos`,
        success_count: aggregatedResults.success.length,
        not_found_count: aggregatedResults.not_found.length,
        error_count: aggregatedResults.errors.length,
        details: aggregatedResults
      };
      
      setResults(finalResults);
      
      if (finalResults.success_count > 0) {
        toast.success(`${finalResults.success_count} logos actualizados con éxito!`);
      }
      if (finalResults.not_found_count > 0) {
        toast.warning(`${finalResults.not_found_count} clientes no encontrados`);
      }

      setLogoFiles([]);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Error en la subida: " + getApiErrorMessage(error));
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const DropZone = ({ onDrop, onFileSelect, fileCount, type }) => (
    <div
      onDrop={onDrop}
      onDragOver={(e) => e.preventDefault()}
      className="border-2 border-dashed border-stone-300 rounded-xl p-8 text-center hover:border-[#009392] hover:bg-teal-50/50 transition-colors cursor-pointer"
      onClick={() => document.getElementById(`file-input-${type}`).click()}
    >
      <input
        id={`file-input-${type}`}
        type="file"
        multiple
        accept="image/*"
        onChange={onFileSelect}
        className="hidden"
      />
      <CloudArrowUp size={48} className="mx-auto text-stone-400 mb-4" />
      <p className="text-lg font-medium text-stone-700">
        Arrastra las imágenes aquí
      </p>
      <p className="text-sm text-stone-500 mt-1">
        o haz clic para seleccionar
      </p>
      {fileCount > 0 && (
        <p className="text-sm text-[#009392] mt-3 font-medium">
          {fileCount} {fileCount === 1 ? 'imagen seleccionada' : 'imágenes seleccionadas'}
        </p>
      )}
    </div>
  );

  const FileList = ({ files, onRemove }) => (
    <div className="mt-4 max-h-64 overflow-y-auto space-y-2">
      {files.map((file, index) => (
        <div key={index} className="flex items-center gap-3 p-2 bg-stone-50 rounded-lg">
          <ImageIcon size={20} className="text-stone-400" />
          <span className="flex-1 text-sm text-stone-700 truncate">{file.name}</span>
          <span className="text-xs text-stone-400">{(file.size / 1024).toFixed(1)} KB</span>
          <button
            onClick={() => onRemove(index)}
            className="p-1 hover:bg-red-100 rounded text-stone-400 hover:text-red-500"
          >
            <XCircle size={18} />
          </button>
        </div>
      ))}
    </div>
  );

  const ResultsPanel = ({ results }) => {
    if (!results) return null;

    return (
      <div className="mt-6 space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <CheckCircle size={24} className="mx-auto text-green-500 mb-2" />
            <p className="text-2xl font-bold text-green-600">{results.success_count}</p>
            <p className="text-sm text-green-700">Exitosos</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
            <Warning size={24} className="mx-auto text-amber-500 mb-2" />
            <p className="text-2xl font-bold text-amber-600">{results.not_found_count}</p>
            <p className="text-sm text-amber-700">No encontrados</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <XCircle size={24} className="mx-auto text-red-500 mb-2" />
            <p className="text-2xl font-bold text-red-600">{results.error_count}</p>
            <p className="text-sm text-red-700">Errores</p>
          </div>
        </div>

        {/* Details */}
        {results.details?.not_found?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="font-medium text-amber-800 mb-2">Productos no encontrados:</h4>
            <ul className="text-sm text-amber-700 space-y-1 max-h-40 overflow-y-auto">
              {results.details.not_found.map((item, i) => (
                <li key={i}>• {item.filename} (códigos intentados: {item.codes_tried?.join(", ") || item.code_searched || item.name_searched})</li>
              ))}
            </ul>
          </div>
        )}

        {results.details?.errors?.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-medium text-red-800 mb-2">Errores:</h4>
            <ul className="text-sm text-red-700 space-y-1">
              {results.details.errors.map((item, i) => (
                <li key={i}>• {item.filename}: {item.error}</li>
              ))}
            </ul>
          </div>
        )}

        {results.details?.success?.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-800 mb-2">Actualizados correctamente:</h4>
            <ul className="text-sm text-green-700 space-y-1 max-h-40 overflow-y-auto">
              {results.details.success.map((item, i) => (
                <li key={i}>• {item.code || item.client_name}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <PageHeader
          icon={CloudArrowUp}
          title="Carga Masiva de Imágenes"
          subtitle="Actualiza imágenes de productos y logos de clientes"
        />
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-blue-800 mb-2">Instrucciones:</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Productos:</strong> El nombre del archivo debe ser el código del producto (ej: B07GRDV0002.jpg)</li>
          <li>• <strong>Logos:</strong> El nombre del archivo debe ser el nombre del cliente (ej: Real Marão.png)</li>
          <li>• Puedes subir múltiples imágenes a la vez</li>
          <li>• Las imágenes se asociarán automáticamente a los registros existentes</li>
        </ul>
      </div>

      <Tabs defaultValue="products" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="products">Imágenes de Productos</TabsTrigger>
          <TabsTrigger value="logos">Logos de Clientes</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <DropZone
              onDrop={handleProductDrop}
              onFileSelect={(e) => handleProductDrop(e)}
              fileCount={productFiles.length}
              type="products"
            />
            
            {productFiles.length > 0 && (
              <>
                <FileList files={productFiles} onRemove={removeProductFile} />
                
                {uploading && uploadProgress.total > 0 && (
                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between text-sm text-stone-600">
                      <span>
                        {uploadProgress.phase === 'compressing' 
                          ? 'Comprimiendo imágenes...' 
                          : 'Subiendo imágenes...'}
                      </span>
                      <span>{uploadProgress.current} / {uploadProgress.total}</span>
                    </div>
                    <Progress value={(uploadProgress.current / uploadProgress.total) * 100} className="h-2" />
                    <p className="text-xs text-stone-400">
                      {uploadProgress.phase === 'compressing' 
                        ? 'Reduciendo tamaño para evitar errores de servidor...' 
                        : `Lote ${Math.ceil(uploadProgress.current / BATCH_SIZE)} de ${Math.ceil(uploadProgress.total / BATCH_SIZE)}`}
                    </p>
                  </div>
                )}
                
                <Button
                  onClick={uploadProducts}
                  disabled={uploading}
                  className="w-full mt-4 bg-[#009392] hover:bg-[#007b7a]"
                >
                  {uploading ? (
                    <>
                      <LoadingSpinner size={16} className="mr-2" />
                      {uploadProgress.phase === 'compressing' 
                        ? `Comprimiendo ${uploadProgress.current}/${uploadProgress.total}...`
                        : `Subiendo ${uploadProgress.current}/${uploadProgress.total}...`}
                    </>
                  ) : (
                    <>
                      <CloudArrowUp size={20} className="mr-2" />
                      Subir {productFiles.length} {productFiles.length === 1 ? 'imagen' : 'imágenes'}
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="logos">
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <DropZone
              onDrop={handleLogoDrop}
              onFileSelect={(e) => handleLogoDrop(e)}
              fileCount={logoFiles.length}
              type="logos"
            />
            
            {logoFiles.length > 0 && (
              <>
                <FileList files={logoFiles} onRemove={removeLogoFile} />
                
                {uploading && uploadProgress.total > 0 && (
                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between text-sm text-stone-600">
                      <span>Subiendo logos...</span>
                      <span>{uploadProgress.current} / {uploadProgress.total}</span>
                    </div>
                    <Progress value={(uploadProgress.current / uploadProgress.total) * 100} className="h-2" />
                  </div>
                )}
                
                <Button
                  onClick={uploadLogos}
                  disabled={uploading}
                  className="w-full mt-4 bg-[#009392] hover:bg-[#007b7a]"
                >
                  {uploading ? (
                    <>
                      <LoadingSpinner size={16} className="mr-2" />
                      Subiendo {uploadProgress.current}/{uploadProgress.total}...
                    </>
                  ) : (
                    <>
                      <CloudArrowUp size={20} className="mr-2" />
                      Subir {logoFiles.length} {logoFiles.length === 1 ? 'logo' : 'logos'}
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <ResultsPanel results={results} />
    </div>
  );
}
